import React from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  useTheme,
  alpha,
  styled,
  Fade,
} from "@mui/material";
import SpaIcon from "@mui/icons-material/Spa"; // Ícone: Bem-estar, cuidado
import FavoriteIcon from "@mui/icons-material/Favorite"; // Ícone: Amor, carinho
import GroupIcon from "@mui/icons-material/Group"; // Ícone: Família, grupo
import SavingsIcon from "@mui/icons-material/Savings"; // Ícone: Economia
import SupportAgentIcon from "@mui/icons-material/SupportAgent"; // Ícone: Atendimento
import EventAvailableIcon from "@mui/icons-material/EventAvailable"; // Ícone: Disponibilidade

// Ícone Estilizado - Usando prop transitória $iconColor
const FeatureIconStyled = styled(Box, {
  shouldForwardProp: (prop) => prop !== "iconColor",
})<{ iconColor: string }>(({ theme, iconColor }) => ({
  width: 64,
  height: 64,
  borderRadius: theme.shape.borderRadius,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginBottom: theme.spacing(2.5),
  background: `linear-gradient(145deg, ${alpha(iconColor, 0.15)} 0%, ${alpha(
    iconColor,
    0.05
  )} 100%)`,
  color: iconColor,
  transition: "transform 0.3s ease-in-out",
}));

const features = [
  {
    icon: <SpaIcon />, // Ícone de bem-estar
    title: "Cuidado Humanizado",
    description:
      "Nossa prioridade é o seu bem-estar, com atendimento próximo e acolhedor em todas as etapas.",
    color: "#F06292", // Rosa escuro (secondary.dark)
    delay: 200,
  },
  {
    icon: <FavoriteIcon />, // Ícone de coração
    title: "Planos para Quem Você Ama",
    description:
      "Opções flexíveis que se adaptam às necessidades da sua família, garantindo proteção completa.",
    color: "#4FC3F7", // Azul principal (primary.dark)
    delay: 400,
  },
  {
    icon: <GroupIcon />, // Ícone de grupo
    title: "Ampla Rede Credenciada",
    description:
      "Acesso aos melhores hospitais, clínicas e laboratórios perto de você com facilidade.",
    color: "#F48FB1", // Rosa claro (secondary.main)
    delay: 600,
  },
  {
    icon: <SavingsIcon />, // Ícone de economia
    title: "Preços Acessíveis",
    description:
      "Excelente custo-benefício para que você tenha tranquilidade sem pesar no orçamento.",
    color: "#81D4FA", // Azul claro (primary.main)
    delay: 800,
  },
  {
    icon: <SupportAgentIcon />, // Ícone de suporte
    title: "Suporte Dedicado",
    description:
      "Conte com nossa equipe para tirar dúvidas e auxiliar sempre que precisar, de forma rápida.",
    color: "#F06292", // Rosa escuro (secondary.dark)
    delay: 1000,
  },
  {
    icon: <EventAvailableIcon />, // Ícone de disponibilidade
    title: "Agendamento Facilitado",
    description:
      "Marque consultas e exames de forma simples e prática através de nossos canais digitais.",
    color: "#4FC3F7", // Azul principal (primary.dark)
    delay: 1200,
  },
];

export const FeaturesSection = () => {
  const theme = useTheme();

  return (
    <Box
      id="beneficios" // Adicionado ID para navegação
      sx={{
        py: { xs: 8, md: 12 },
        backgroundColor: theme.palette.background.paper,
        position: "relative",
      }}
    >
      <Container maxWidth="lg">
        <Box sx={{ textAlign: "center", mb: { xs: 6, md: 8 } }}>
          <Typography
            variant="overline"
            sx={{
              color: theme.palette.primary.dark,
              fontWeight: 600,
              mb: 1,
              display: "block",
            }}
          >
            Nossos Diferenciais
          </Typography>
          <Typography
            variant="h2"
            component="h2"
            sx={{
              fontWeight: 700,
              mb: 2,
              fontSize: { xs: "2.2rem", md: "2.8rem" },
              color: theme.palette.text.primary,
            }}
          >
            Feito com Carinho para Você
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: theme.palette.text.secondary,
              maxWidth: "700px",
              mx: "auto",
              lineHeight: 1.7,
            }}
          >
            Descubra as vantagens de ter um plano de saúde que se preocupa
            genuinamente com o seu bem-estar e o de sua família.
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Fade in={true} timeout={feature.delay}>
                <Card
                  elevation={0}
                  sx={{
                    height: "100%",
                    textAlign: "center",
                    p: 3,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    backgroundColor: alpha(
                      theme.palette.background.default,
                      0.5
                    ),
                    transition: "transform 0.3s ease, box-shadow 0.3s ease",
                    border: `1px solid ${theme.palette.divider}`,
                    "&:hover": {
                      transform: "translateY(-8px)",
                      boxShadow: `0 12px 25px ${alpha("#B0BEC5", 0.12)}`,
                      borderColor: feature.color,
                      "& .feature-icon": {
                        transform: "scale(1.1)",
                      },
                    },
                  }}
                >
                  <FeatureIconStyled
                    className="feature-icon"
                    iconColor={feature.color}
                  >
                    {feature.icon}
                  </FeatureIconStyled>
                  <CardContent sx={{ p: 0 }}>
                    <Typography
                      variant="h6"
                      component="h3"
                      sx={{
                        fontWeight: 600,
                        mb: 1.5,
                        color: theme.palette.text.primary,
                      }}
                    >
                      {feature.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ lineHeight: 1.6 }}
                    >
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Fade>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};
